import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import type { AppDispatch } from "@/app/store";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { thunkInitializeAuth } from "@/modules/auth/store/authThunks";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { DEFAULT_GRADIENT_ID } from "../constants/cardGradients";
import { BrandingService } from "../services/brandingService";
import type { CardStylesConfig, OrgBrandingState } from "../types/branding.types";

const EMPTY_DESIGN: CardStylesConfig = {
  gradient_id: null,
  secondary_logo_url: null,
  secondary_logo_scale: null,
};

const RESET_DESIGN: CardStylesConfig = {
  gradient_id: DEFAULT_GRADIENT_ID,
  secondary_logo_url: null,
  secondary_logo_scale: 1,
};

// `orgIdOverride` lets internal Coloca users (no self-org) operate via the
// `?org_id` query param. After every mutation we re-init auth so every
// `useOrgDesign()` consumer re-renders.
export function useOrgBranding(orgIdOverride?: string | null): OrgBrandingState {
  const { user } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const orgId =
    (orgIdOverride && orgIdOverride.trim().length > 0 ? orgIdOverride : user?.organization?.id) ?? null;

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const design: CardStylesConfig = user?.organization?.card_styles_config ?? EMPTY_DESIGN;
  const canCustomize = Boolean(user?.organization?.can_customize_card_design);

  const reload = useCallback(async () => {
    await (dispatch(thunkInitializeAuth()) as unknown as Promise<void>);
  }, [dispatch]);

  const save = useCallback(
    async (patch: CardStylesConfig) => {
      if (!orgId) return;
      setIsSaving(true);
      setError(null);
      try {
        await BrandingService.updateCardStyles(orgId, patch);
        await (dispatch(thunkInitializeAuth()) as unknown as Promise<void>);
      } catch (err) {
        const message = HttpApiService.getErrorMessage(err) || "Could not save branding";
        setError(message);
        throw new Error(message);
      } finally {
        setIsSaving(false);
      }
    },
    [orgId, dispatch],
  );

  const uploadLogo = useCallback(
    async (file: File): Promise<string> => {
      if (!orgId) throw new Error("No organization in context");
      setIsUploadingLogo(true);
      setError(null);
      try {
        const { url } = await BrandingService.uploadLogo(orgId, file);
        return url;
      } catch (err) {
        const message = HttpApiService.getErrorMessage(err) || "Could not upload logo";
        setError(message);
        throw new Error(message);
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [orgId],
  );

  const reset = useCallback(async () => {
    await save(RESET_DESIGN);
  }, [save]);

  return {
    design,
    canCustomize,
    isLoading: false,
    isSaving,
    isUploadingLogo,
    error,
    reload,
    save,
    uploadLogo,
    reset,
  };
}
